/**
 * Set one template's monthly budget.
 *
 * The question an admin is really asking while typing a number here is "can
 * the plan afford this?", so the answer is on screen before they press save:
 * the plan bar redraws with this template lit, and the moment the total runs
 * past the plan the editor says by how much, what that excess turns into
 * (pages billed as overage), and offers the largest amount that still fits.
 *
 * Going over is allowed (overage is a legitimate choice), so it is stated
 * plainly rather than alarmed about.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, BellRing, Check, CircleDollarSign, Sparkles, Trash2, TrendingUp, X } from 'lucide-react'
import { Segmented, useBodyScrollLock, usePrefersReducedMotion } from './BillingPrimitives'
import BudgetAllocationBar from './BudgetAllocationBar'
import { formatInt } from '../../utils/billing'
import {
  ALERT_OPTIONS, DEFAULT_ALERT_AT, fromDisplay, pagesForAmount, parseMoneyInput, previewChange, segmentColor, toDisplay,
} from '../../utils/budgets'

function roundForInput(n) {
  const v = Math.round((Number(n) || 0) * 100) / 100
  return Number.isInteger(v) ? String(v) : v.toFixed(2)
}

export default function BudgetEditor({
  row, budgets, names, pool, plan, chart, displayCode, format, formatPrecise, onSave, onRemove, onClose,
}) {
  const open = Boolean(row)
  const reduced = usePrefersReducedMotion()
  const panelRef = useRef(null)
  const inputRef = useRef(null)
  const inputId = useId()
  useBodyScrollLock(open)

  const nativeCode = pool?.currency?.code || 'GBP'
  const [text, setText] = useState('')
  const [alertAt, setAlertAt] = useState(DEFAULT_ALERT_AT)

  useEffect(() => {
    if (!row) return
    setText(row.amount > 0 ? roundForInput(toDisplay(row.amount, nativeCode, displayCode)) : '')
    setAlertAt(row.alertAt || DEFAULT_ALERT_AT)
    // Whatever the admin types, the field is where the work is.
    const id = requestAnimationFrame(() => { inputRef.current?.focus(); inputRef.current?.select() })
    return () => cancelAnimationFrame(id)
  }, [row, nativeCode, displayCode])

  useEffect(() => {
    if (!open) return
    const onKey = e => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const previous = document.activeElement
    return () => {
      document.removeEventListener('keydown', onKey)
      previous?.focus?.()
    }
  }, [open, onClose])

  const typed = parseMoneyInput(text)
  const invalid = text.trim() !== '' && typed === null
  const amount = typed === null ? 0 : fromDisplay(typed, nativeCode, displayCode)

  const preview = useMemo(
    () => (row ? previewChange(budgets, row.id, amount, pool, plan) : null),
    [budgets, row, amount, pool, plan],
  )

  if (!open || !pool || !preview) return null

  const { after, headroom, pushesOver, addedOver, exceedsPlanAlone } = preview
  const isOver = after.overPlan > 0
  // Early in a cycle the projection is one upload extrapolated: no warning from it.
  const belowProjection = amount > 0 && row.projected > amount && plan?.usage?.projection_reliable !== false

  // Slider: wide enough to drag past the plan and see what that means.
  const sliderMax = Math.max(
    pool.fee * 1.25,
    amount * 1.1,
    row.projected * 1.5,
    1,
  )
  const step = sliderMax > 20_000 ? 50 : sliderMax > 2_000 ? 10 : 1
  const headroomPct = Math.min(100, (headroom / sliderMax) * 100)
  const trackFill = Math.min(100, (amount / sliderMax) * 100)
  // Filled to the amount; the unfilled track is a shade darker up to the
  // headroom, so where the plan runs out is visible without colouring it red.
  const trackStyle = {
    background: `linear-gradient(to right,
      #2563eb 0%, #2563eb ${trackFill}%,
      rgb(148 163 184 / 0.45) ${trackFill}%, rgb(148 163 184 / 0.45) ${Math.max(trackFill, headroomPct)}%,
      rgb(148 163 184 / 0.2) ${Math.max(trackFill, headroomPct)}%, rgb(148 163 184 / 0.2) 100%)`,
    '--budget-thumb': '#1d4ed8',
  }

  const setNative = v => setText(roundForInput(toDisplay(v, nativeCode, displayCode)))

  const suggestions = [
    row.projected > 0 && {
      key: 'projected',
      label: 'Projected + 15%',
      value: Math.ceil(row.projected * 1.15),
      hint: `This billing month is heading for ${format(row.projected)}`,
    },
    headroom > 0 && {
      key: 'headroom',
      label: 'All remaining headroom',
      value: Math.floor(headroom),
      hint: 'The most this template can take without going over plan',
    },
    { key: 'p10', label: '10% of plan', value: Math.round(pool.fee * 0.1) },
    { key: 'p25', label: '25% of plan', value: Math.round(pool.fee * 0.25) },
  ].filter(Boolean)

  // Each segment is someone's budget; the one being edited carries the draft.
  const segments = Object.entries({ ...budgets, [row.id]: { amount } })
    .filter(([, b]) => Number(b?.amount) > 0)
    .sort(([a], [b]) => (a === row.id ? -1 : b === row.id ? 1 : 0))
    .map(([id, b], i) => ({
      id,
      label: id === row.id ? row.name : (names?.get(id) || `Template #${id}`),
      amount: Number(b.amount),
      color: id === row.id ? '#2563eb' : segmentColor(chart, i),
    }))

  const save = () => {
    if (invalid) { inputRef.current?.focus(); return }
    onSave(row, { amount, alertAt })
  }

  return createPortal(
    <>
      <button
        type="button"
        aria-label="Close budget editor"
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-gray-900/50 dark:bg-black/70 backdrop-blur-[2px] ${reduced ? '' : 'animate-backdrop-in'}`}
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Budget for ${row.name}`}
        className={`fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[480px] bg-white dark:bg-gray-900
                    border-l border-gray-200 dark:border-gray-800 shadow-2xl flex flex-col focus:outline-none
                    ${reduced ? '' : 'animate-drawer-in'}`}
      >
        {/* ---- header ---- */}
        <header className="flex items-start gap-3 p-5 border-b border-gray-200 dark:border-gray-800
                           bg-gradient-to-br from-navy-50/80 via-white to-white
                           dark:from-blue-400/[0.07] dark:via-gray-900 dark:to-gray-900">
          <span className="p-2 rounded-xl bg-navy-600/10 dark:bg-navy-400/10 text-navy-700 dark:text-navy-300">
            <CircleDollarSign className="w-5 h-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.09em] text-gray-400 dark:text-gray-500">
              Monthly template budget
            </p>
            <h2 className="text-base font-bold text-gray-900 dark:text-gray-100 mt-0.5 truncate">{row.name}</h2>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">Resets with each billing month</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200
                       hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* ---- this cycle so far ---- */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: 'Spent so far', value: format(row.spent) },
              { label: 'Projected', value: format(row.projected), tone: belowProjection ? 'text-amber-600 dark:text-amber-400' : '' },
              { label: 'Pages', value: formatInt(row.pages) },
            ].map((f, i) => (
              <div
                key={f.label}
                className="rounded-lg bg-gray-50 dark:bg-gray-800/60 px-3 py-2.5 animate-budget-rise"
                style={{ '--rise-delay': `${80 + i * 50}ms` }}
              >
                <p className="text-[10px] text-gray-500 dark:text-gray-400">{f.label}</p>
                <p className={`text-sm font-semibold tabular-nums mt-0.5 ${f.tone || 'text-gray-900 dark:text-gray-100'}`}>{f.value}</p>
              </div>
            ))}
          </div>

          {/* ---- the amount ---- */}
          <div>
            <label htmlFor={inputId} className="text-xs font-semibold text-gray-700 dark:text-gray-300">
              Budget per month
            </label>
            <div
              className={`mt-1.5 flex items-center rounded-xl border bg-white dark:bg-gray-950 px-3.5 transition-colors duration-200
                          ${invalid
                            ? 'border-rose-400 dark:border-rose-500'
                            : 'border-gray-200 dark:border-gray-700 focus-within:border-navy-500 dark:focus-within:border-navy-400 focus-within:ring-2 focus-within:ring-navy-500/15'}`}
            >
              <span className="text-xl font-semibold text-gray-400 dark:text-gray-500">
                {displayCode === 'USD' ? '$' : '£'}
              </span>
              <input
                ref={inputRef}
                id={inputId}
                inputMode="decimal"
                autoComplete="off"
                value={text}
                placeholder="0"
                onChange={e => setText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); save() } }}
                aria-invalid={invalid}
                aria-describedby={`${inputId}-hint`}
                className="flex-1 min-w-0 bg-transparent py-3 pl-1.5 text-2xl font-bold tabular-nums
                           text-gray-900 dark:text-gray-100 placeholder:text-gray-300 dark:placeholder:text-gray-600
                           focus:outline-none"
              />
              <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">/ month</span>
            </div>
            <p id={`${inputId}-hint`} className={`mt-1.5 text-[11px] ${invalid ? 'text-rose-600 dark:text-rose-400' : 'text-gray-500 dark:text-gray-400'}`}>
              {invalid
                ? 'Enter an amount such as 1200, 1,200 or 1.2k.'
                : amount > 0
                  ? <>≈ {formatInt(pagesForAmount(amount, pool))} pages at the plan’s {formatPrecise(pool.planRate)} per page</>
                  : 'Leave empty for no budget.'}
            </p>

            <input
              type="range"
              aria-label="Budget amount"
              min={0}
              max={toDisplay(sliderMax, nativeCode, displayCode)}
              step={step}
              value={Math.min(toDisplay(amount, nativeCode, displayCode), toDisplay(sliderMax, nativeCode, displayCode))}
              onChange={e => setText(e.target.value === '0' ? '' : e.target.value)}
              className="budget-range w-full mt-4"
              style={trackStyle}
            />
            <div className="mt-1.5 flex justify-between text-[10px] text-gray-400 dark:text-gray-500 tabular-nums">
              <span>{format(0)}</span>
              <span>Fits the plan up to {format(headroom)}</span>
              <span>{format(sliderMax)}</span>
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {suggestions.map((s, i) => (
                <button
                  key={s.key}
                  type="button"
                  title={s.hint}
                  onClick={() => setNative(s.value)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium
                             bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300
                             hover:bg-navy-50 hover:text-navy-700 dark:hover:bg-navy-800/50 dark:hover:text-navy-200
                             transition-colors animate-budget-pop
                             focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
                  style={{ animationDelay: `${160 + i * 40}ms` }}
                >
                  {s.key === 'projected' && <Sparkles className="w-3 h-3" />}
                  {s.label}
                  <span className="tabular-nums text-gray-400 dark:text-gray-500">{format(s.value)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ---- alert ---- */}
          <div>
            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
              <BellRing className="w-3.5 h-3.5 text-gray-400" />
              Flag when spend reaches
            </p>
            <div className="mt-2">
              <Segmented
                options={ALERT_OPTIONS.map(v => ({ value: v, label: `${v}%` }))}
                value={alertAt}
                onChange={setAlertAt}
                ariaLabel="Alert threshold"
              />
            </div>
            {amount > 0 && (
              <p className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                Marked “Past alert” from {format(amount * (alertAt / 100))} spent.
              </p>
            )}
          </div>

          {/* ---- impact on the plan ---- */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">Impact on your {pool.planName} plan</p>
              <p className="text-[11px] tabular-nums text-gray-500 dark:text-gray-400">
                <span className="font-semibold text-gray-900 dark:text-gray-100">{format(after.allocated)}</span>{' '}
                of {format(pool.fee)}
              </p>
            </div>
            <div className="mt-2">
              <BudgetAllocationBar
                segments={segments}
                fee={pool.fee}
                format={format}
                highlightId={row.id}
                height="h-3"
                label="Budgets after this change"
              />
            </div>

            <div aria-live="polite">
              {isOver ? (
                <div key="over" className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 animate-panel-in">
                  <p className="flex items-start gap-2 text-[12.5px] font-medium text-gray-900 dark:text-gray-100">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-500" />
                    {exceedsPlanAlone
                      ? <>This budget is {format(amount - pool.fee)} more than your whole plan</>
                      : <>{format(after.overPlan)} over your plan</>}
                  </p>
                  <p className="mt-1 pl-[22px] text-[11.5px] leading-relaxed text-gray-500 dark:text-gray-400">
                    {pushesOver && addedOver < after.overPlan && <>{format(addedOver)} of it comes from this change. </>}
                    The extra {format(after.overPlan)} is about {formatInt(after.extraPages)} pages beyond your allowance,
                    which would be billed as overage at {formatPrecise(pool.overageRate)} each ({format(after.extraPagesCost, { pence: true })}).
                    {after.covers && <> {after.covers.name} ({format(after.covers.fee)}/month) would cover it.</>}
                  </p>
                  {headroom > 0 && Math.floor(headroom) < amount && (
                    <button
                      type="button"
                      onClick={() => setNative(Math.floor(headroom))}
                      className="mt-2 ml-[22px] text-xs font-medium text-navy-700 dark:text-navy-300 hover:underline underline-offset-2
                                 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded"
                    >
                      Use {format(Math.floor(headroom))} to stay within plan
                    </button>
                  )}
                </div>
              ) : amount > 0 ? (
                <p key="fits" className="mt-3 text-[11.5px] text-gray-500 dark:text-gray-400 flex items-center gap-1.5 animate-panel-in">
                  <Check className="w-3.5 h-3.5 text-emerald-500" />
                  Fits the plan, with {format(after.unallocated)} left unallocated.
                </p>
              ) : null}
            </div>
          </div>

          {belowProjection && (
            <p className="flex items-start gap-2 text-[11.5px] leading-relaxed text-amber-700 dark:text-amber-400 animate-panel-in">
              <TrendingUp className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              This template is projected to spend {format(row.projected)} this billing month, so it would run past this
              budget before the billing month ends.
            </p>
          )}
        </div>

        {/* ---- footer ---- */}
        <footer className="flex items-center gap-2 p-4 border-t border-gray-200 dark:border-gray-800">
          {row.amount > 0 && (
            <button
              type="button"
              onClick={() => onRemove(row)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium
                         text-gray-500 hover:text-rose-600 dark:text-gray-400 dark:hover:text-rose-400
                         hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Remove
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300
                         hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={invalid}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm
                         bg-navy-700 hover:bg-navy-800 dark:bg-navy-600 dark:hover:bg-navy-500
                         transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-navy-500
                         dark:focus-visible:ring-offset-gray-900"
            >
              {amount <= 0 && row.amount > 0 ? 'Clear budget' : 'Save budget'}
            </button>
          </div>
        </footer>
      </aside>
    </>,
    document.body,
  )
}
